/**
 * MessageComposer Component
 *
 * A lightweight message composer for the CommandPalette with @mention support.
 * Allows operators to send quick messages to other operators.
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Input, Button, Typography, Tag, Spin, message } from 'antd';
import { SendOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';
import { search, type OperatorListItem } from '@/services/operators';
import { trpc } from '@/trpc';
import { useCurrentPageContext } from '@/hooks/useCurrentPageContext';
import styles from './MessageComposer.module.less';

const { Text } = Typography;
const { TextArea } = Input;

export type MessageComposerProps = {
  onClose: () => void;
  onSuccess?: () => void;
};

type SelectedOperator = {
  id: string;
  name: string;
};

const MAX_MESSAGE_LENGTH = 2000;
const MAX_RECIPIENTS = 20;

export const MessageComposer: React.FC<MessageComposerProps> = ({ onClose, onSuccess }) => {
  const pageContext = useCurrentPageContext();
  
  // State
  const [messageText, setMessageText] = useState('');
  const [selectedOperators, setSelectedOperators] = useState<SelectedOperator[]>([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionResults, setMentionResults] = useState<OperatorListItem[]>([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  
  // Refs
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  const mentionSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const mentionSearchIdRef = useRef(0);

  // tRPC mutation
  const sendMessageMutation = trpc.inbox.sendMessage.useMutation({
    onSuccess: () => {
      message.success('Message sent successfully');
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      message.error(error.message || 'Failed to send message');
    }
  });

  // Character count
  const charCount = messageText.length;
  const isOverLimit = charCount > MAX_MESSAGE_LENGTH;
  const canSend = messageText.trim().length > 0 && 
                  selectedOperators.length > 0 && 
                  !isOverLimit && 
                  !sendMessageMutation.isPending;

  // Handle @mention detection
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessageText(value);

    // Detect @ mention
    const cursorPos = e.target.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);

    if (atMatch) {
      const query = atMatch[1];
      setMentionQuery(query);
      setShowMentionDropdown(true);
      setMentionSelectedIndex(0);

      // Debounced search
      if (mentionSearchTimeout.current) {
        clearTimeout(mentionSearchTimeout.current);
      }
      mentionSearchTimeout.current = setTimeout(async () => {
        const currentSearchId = ++mentionSearchIdRef.current;
        if (query.length >= 1) {
          setMentionLoading(true);
          try {
            const response = await search({ name: query, status: 'active' });
            const operators = response?.data ?? [];
            // Filter out already selected operators
            const filtered = operators.filter(
              op => !selectedOperators.some(sel => sel.id === op.id)
            );
            if (currentSearchId === mentionSearchIdRef.current) setMentionResults(filtered.slice(0, 8));
          } catch {
            setMentionResults([]);
          } finally {
            setMentionLoading(false);
          }
        } else {
          // Show recent/all active operators
          setMentionLoading(true);
          try {
            const response = await search({ status: 'active', pageSize: 8 });
            const operators = response?.data ?? [];
            const filtered = operators.filter(
              op => !selectedOperators.some(sel => sel.id === op.id)
            );
            if (currentSearchId === mentionSearchIdRef.current) setMentionResults(filtered.slice(0, 8));
          } catch {
            setMentionResults([]);
          } finally {
            setMentionLoading(false);
          }
        }
      }, 150);
    } else {
      setShowMentionDropdown(false);
      setMentionResults([]);
    }
  }, [selectedOperators]);

  // Select operator from mention dropdown
  const selectOperator = useCallback((operator: OperatorListItem) => {
    if (selectedOperators.length >= MAX_RECIPIENTS) {
      message.warning(`Maximum ${MAX_RECIPIENTS} recipients allowed`);
      return;
    }

    // Add to selected operators
    setSelectedOperators(prev => [...prev, { id: operator.id, name: operator.name }]);

    // Remove the @query from text
    const cursorPos = textAreaRef.current?.selectionStart || messageText.length;
    const textBeforeCursor = messageText.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (atMatch) {
      const startPos = cursorPos - atMatch[0].length;
      const newText = messageText.slice(0, startPos) + messageText.slice(cursorPos);
      setMessageText(newText);
    }

    setShowMentionDropdown(false);
    setMentionResults([]);
    textAreaRef.current?.focus();
  }, [messageText, selectedOperators]);

  // Remove operator from selection
  const removeOperator = useCallback((operatorId: string) => {
    setSelectedOperators(prev => prev.filter(op => op.id !== operatorId));
  }, []);

  // Send message
  const handleSend = useCallback(() => {
    if (!canSend) return;

    sendMessageMutation.mutate({
      message: messageText.trim(),
      recipientIds: selectedOperators.map(op => op.id),
      pageUrl: pageContext.url,
      pageTitle: pageContext.title
    });
  }, [canSend, messageText, selectedOperators, pageContext, sendMessageMutation]);

  // Keyboard navigation for mention dropdown
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionDropdown && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIndex(prev => 
          prev < mentionResults.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIndex(prev => 
          prev > 0 ? prev - 1 : mentionResults.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const selected = mentionResults[mentionSelectedIndex];
        if (selected) {
          selectOperator(selected);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
      }
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (canSend) {
        handleSend();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [showMentionDropdown, mentionResults, mentionSelectedIndex, selectOperator, canSend, onClose, handleSend]);

  // Focus textarea on mount
  useEffect(() => {
    setTimeout(() => {
      textAreaRef.current?.focus();
    }, 100);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (mentionSearchTimeout.current) {
        clearTimeout(mentionSearchTimeout.current);
      }
    };
  }, []);

  return (
    <div className={styles.composer}>
      {/* Page Context Badge */}
      <div className={styles.pageContext}>
        <LinkOutlined className={styles.pageContextIcon} />
        <Text type="secondary" ellipsis className={styles.pageContextText}>
          Sharing: {pageContext.title}
        </Text>
      </div>

      {/* Selected Recipients */}
      {selectedOperators.length > 0 && (
        <div className={styles.recipients}>
          <Text type="secondary" className={styles.recipientsLabel}>To:</Text>
          <div className={styles.recipientTags}>
            {selectedOperators.map(op => (
              <Tag
                key={op.id}
                closable
                onClose={() => removeOperator(op.id)}
                className={styles.recipientTag}
              >
                <UserOutlined /> {op.name}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className={styles.inputWrapper}>
        <TextArea
          ref={textAreaRef}
          value={messageText}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder="Type @ to mention operators..."
          autoSize={{ minRows: 3, maxRows: 8 }}
          className={styles.textArea}
          maxLength={MAX_MESSAGE_LENGTH + 100} // Allow some overflow for UX
        />

        {/* Mention Dropdown */}
        {showMentionDropdown && (
          <div ref={mentionDropdownRef} className={styles.mentionDropdown}>
            {mentionLoading ? (
              <div className={styles.mentionLoading}>
                <Spin size="small" />
                <Text type="secondary">Searching operators...</Text>
              </div>
            ) : mentionResults.length > 0 ? (
              mentionResults.map((op, index) => (
                <div
                  key={op.id}
                  className={`${styles.mentionItem} ${index === mentionSelectedIndex ? styles.selected : ''}`}
                  onClick={() => selectOperator(op)}
                  onMouseEnter={() => setMentionSelectedIndex(index)}
                >
                  <UserOutlined className={styles.mentionIcon} />
                  <div className={styles.mentionContent}>
                    <Text strong>{op.name}</Text>
                    <Text type="secondary" className={styles.mentionEmail}>{op.email}</Text>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.mentionEmpty}>
                <Text type="secondary">No operators found</Text>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <Text type="secondary" className={styles.charCount}>
            {charCount} / {MAX_MESSAGE_LENGTH}
            {isOverLimit && <Text type="danger"> (over limit)</Text>}
          </Text>
        </div>
        <div className={styles.footerRight}>
          <Text type="secondary" className={styles.hint}>
            <kbd>⌘</kbd>+<kbd>Enter</kbd> to send
          </Text>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={sendMessageMutation.isPending}
            disabled={!canSend}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessageComposer;
